"use client";

import type { useChat } from "@ai-sdk/react";
import { Trans } from "@lingui/react/macro";
import { useQuery } from "@urql/next";
import { getToolName, isToolUIPart, type ToolUIPart } from "ai";
import { graphql } from "gql.tada";
import { Skeleton } from "@/components/ui/skeleton";
import type { AgentUIMessage } from "@/lib/ai/types";
import { ApproveButton } from "../ui/approve-button";
import { DenyButton } from "../ui/deny-button";

// プロジェクト名を表示するために取得する
const DuplicateProjectConfirmQuery = graphql(`
  query DuplicateProjectConfirmQuery($id: ID!) {
    node(id: $id) {
      __typename
      ... on IndexWorkerProject { name }
      ... on SearchAnalyticsProject { name }
      ... on UserAnalyticsProject { name }
    }
  }
`);

type DuplicateProjectInput = {
  projectId: string;
};

type Props = {
  part: ToolUIPart;
  addToolResult: ReturnType<typeof useChat<AgentUIMessage>>["addToolResult"];
  sendMessage: ReturnType<typeof useChat<AgentUIMessage>>["sendMessage"];
};

export function DuplicateProjectConfirm({
  part,
  addToolResult,
  sendMessage,
}: Props) {
  const input = (part.input ?? {}) as Partial<DuplicateProjectInput>;
  const projectId = input.projectId ?? "";

  const [{ data, fetching }] = useQuery({
    query: DuplicateProjectConfirmQuery,
    variables: { id: projectId },
    pause: !projectId,
  });
  const projectName =
    data?.node?.__typename === "IndexWorkerProject" ||
    data?.node?.__typename === "SearchAnalyticsProject" ||
    data?.node?.__typename === "UserAnalyticsProject"
      ? data?.node?.name
      : "Unknown";

  if (!(isToolUIPart(part) && getToolName(part) === "duplicate_project"))
    return null;
  if (part.state !== "input-available") return null;

  return (
    <div className="p-4 text-sm">
      <div className="mb-2 font-medium">
        <Trans>Do you want to duplicate this project?</Trans>
      </div>
      <ul className="mb-4 list-disc pl-5 space-y-1">
        <li>
          <Trans>Project Name: </Trans>
          {fetching ? (
            <Skeleton className="h-4 w-12 bg-gray-400 dark:bg-gray-600" />
          ) : (
            <span className="font-medium">{projectName}</span>
          )}
        </li>
      </ul>
      <div className="flex gap-2">
        <ApproveButton
          toolCallId={part.toolCallId}
          toolName={getToolName(part)}
          addToolResult={addToolResult}
          sendMessage={sendMessage}
        >
          <Trans>Duplicate</Trans>
        </ApproveButton>
        <DenyButton
          toolCallId={part.toolCallId}
          toolName={getToolName(part)}
          addToolResult={addToolResult}
          sendMessage={sendMessage}
        >
          <Trans>Cancel</Trans>
        </DenyButton>
      </div>
    </div>
  );
}
